import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Clock, ArrowRight, X, AlertTriangle, CreditCard, Lock, RefreshCw } from 'lucide-react';

/**
 * TrialBanner — Handles trial countdown and payment-required states.
 * 
 * States:
 * - ACTIVE paid → No banner
 * - TRIAL with days > 0 → Green "X days remaining"
 * - PAYMENT_PENDING → RED "Payment required" + blocks all modules
 * - SUSPENDED → Full overlay block
 */
export function TrialBanner() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [subStatus, setSubStatus] = useState(null);
  const today = new Date().toISOString().slice(0, 10);
  const [dismissed, setDismissed] = useState(() => localStorage.getItem('trial_banner_dismissed') === today);
  const [loaded, setLoaded] = useState(false);

  const handleDismiss = () => {
    setDismissed(true);
    localStorage.setItem('trial_banner_dismissed', today);
  };

  useEffect(() => {
    let active = true;
    (async () => {
      if (!user || !user.academy_id) {
        if (active) setLoaded(true);
        return;
      }
      try {
        const r = await api.get('/subscription/status');
        if (active) setSubStatus(r.data);
      } catch { /* silent — banner just won't render */ }
      finally { if (active) setLoaded(true); }
    })();
    return () => { active = false; };
  }, [user]);

  if (!loaded || !subStatus) return null;

  // Phase 202.bugfix — only academy_owner can act on subscription state. Hide
  // the orange/red banner from employees, coaches, students, parents and
  // super_admin — they cannot pay or sign the renewal anyway.
  if (user?.role !== 'academy_owner') return null;

  // PAYMENT_PENDING — Red banner + redirect to subscription page
  if (subStatus.is_payment_pending || subStatus.status === 'PAYMENT_PENDING') {
    const isOnSubscription = location.pathname === '/subscription';
    const hasGrace = subStatus.grace_active;
    
    // Grace period — show amber warning but DON'T block
    if (hasGrace) {
      return (
        <div className="bg-amber-500 text-white px-4 py-2.5 text-center text-sm font-semibold flex items-center justify-center gap-3 flex-wrap">
          <Clock className="h-4 w-4" />
          <span>Payment pending — Grace period active. Please pay to avoid suspension.</span>
          <Link to="/subscription">
            <Button size="sm" className="bg-white text-amber-700 hover:bg-amber-50 h-7 text-xs font-bold">
              <CreditCard className="h-3.5 w-3.5 mr-1" /> Pay Now
            </Button>
          </Link>
        </div>
      );
    }
    
    return (
      <>
        {/* Red payment banner */}
        <div className="bg-red-600 text-white px-4 py-3 text-center text-sm font-semibold flex items-center justify-center gap-3 flex-wrap">
          <AlertTriangle className="h-5 w-5" />
          <span>Your trial has expired. Complete payment to continue using Out-Play.</span>
          {!isOnSubscription && (
            <Link to="/subscription">
              <Button size="sm" className="bg-white text-red-700 hover:bg-red-50 h-7 text-xs font-bold shadow">
                <CreditCard className="h-3.5 w-3.5 mr-1" /> Pay Now
              </Button>
            </Link>
          )}
        </div>
        
        {/* Block overlay on all pages except subscription */}
        {!isOnSubscription && (
          <div className="fixed inset-0 z-[90] bg-black/40 backdrop-blur-[2px] flex items-center justify-center" onClick={() => navigate('/subscription')}>
            <div className="bg-card rounded-2xl shadow-2xl max-w-sm w-full mx-4 p-8 text-center border" onClick={e => e.stopPropagation()}>
              <div className="h-14 w-14 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
                <Lock className="h-7 w-7 text-red-600" />
              </div>
              <h2 className="text-xl font-display font-bold mb-2">Payment Required</h2>
              <p className="text-sm text-muted-foreground mb-5">Your 15-day trial has ended. Pay the Pro plan invoice to unlock all features.</p>
              <Link to="/subscription">
                <Button className="w-full h-11 bg-emerald-600 hover:bg-emerald-700 text-base rounded-xl">
                  <CreditCard className="h-5 w-5 mr-2" /> Go to Payment
                </Button>
              </Link>
            </div>
          </div>
        )}
      </>
    );
  }

  // ACTIVE paid — no banner
  if (subStatus.status === 'ACTIVE') return null;
  
  // TRIAL with days remaining — green banner
  if (subStatus.status === 'TRIAL' && subStatus.days_remaining > 0 && !dismissed) {
    const urgent = subStatus.days_remaining <= 3;
    return (
      <div className={`${urgent ? 'bg-red-500' : 'bg-emerald-600'} text-white px-4 py-2 text-center text-sm font-medium flex items-center justify-center gap-2`}>
        <Clock className="h-4 w-4" />
        {urgent ? `Trial expires in ${subStatus.days_remaining} day${subStatus.days_remaining !== 1 ? 's' : ''}!` : `Free trial: ${subStatus.days_remaining} days remaining`}
        <Link to="/#pricing"><Button size="sm" variant="secondary" className="h-6 text-xs ml-2">Upgrade <ArrowRight className="ml-1 h-3 w-3" /></Button></Link>
        <button onClick={handleDismiss} className="ml-1 opacity-70 hover:opacity-100"><X className="h-3.5 w-3.5" /></button>
      </div>
    );
  }

  return null;
}

/**
 * SuspensionOverlay — Full block for suspended accounts (payment overdue).
 *
 * Phase 253-Paywall-Harden:
 *   - Lifts the overlay on the `/subscription` route (and its alias)
 *     so an owner can actually COMPLETE the Razorpay flow without
 *     being trapped in the modal.
 *   - Adds a "I've Paid — Check Status" refresh CTA that re-polls
 *     `/subscription/status` immediately (covers the 0-30s window
 *     between Razorpay capture and our webhook fan-out).
 *   - Polls the status endpoint every 12s as a safety net so the
 *     overlay un-mounts automatically once the webhook lands.
 */
const SUSPENSION_OPEN_PATHS = ['/subscription', '/settings/subscription'];

export function SuspensionOverlay() {
  const { user } = useAuth();
  const location = useLocation();
  const [suspended, setSuspended] = useState(false);
  const [academyName, setAcademyName] = useState('');
  const [verifying, setVerifying] = useState(false);
  const navigate = useNavigate();

  // Centralised status fetch so the manual refresh + initial mount
  // share the same code path.
  const checkStatus = async () => {
    try {
      const r = await api.get('/subscription/status');
      const isSuspended = !!(r.data?.is_suspended || r.data?.status === 'SUSPENDED');
      setSuspended(isSuspended);
      if (isSuspended) setAcademyName(r.data?.academy_name || '');
      return isSuspended;
    } catch {
      return false;
    }
  };

  useEffect(() => {
    if (!user || !user.academy_id) return undefined;
    // Phase 202.bugfix — same RBAC posture as TrialBanner. Only the
    // academy_owner can act on a suspension (pay the renewal), so the
    // full-screen overlay is restricted to owners.
    if (user.role !== 'academy_owner') return undefined;
    let active = true;
    (async () => { if (active) await checkStatus(); })();
    // Phase 253-Paywall-Harden — gentle background poll so the modal
    // auto-unlocks within ~12s of the Razorpay webhook landing
    // (without forcing the operator to refresh the page).
    const t = setInterval(() => { if (active) checkStatus(); }, 12000);
    return () => { active = false; clearInterval(t); };
  }, [user]);

  if (!suspended) return null;
  // Phase 253-Paywall-Harden — don't block the subscription routes;
  // owners need them to actually pay. Once payment.captured fires our
  // webhook → poll un-mounts the overlay → access fully restored.
  if (SUSPENSION_OPEN_PATHS.includes(location.pathname)) return null;

  const handleManualSync = async () => {
    setVerifying(true);
    try { await checkStatus(); }
    finally { setTimeout(() => setVerifying(false), 600); }
  };

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="suspension-overlay"
    >
      <div className="bg-card rounded-2xl shadow-2xl max-w-md w-full p-8 text-center border">
        <div className="h-16 w-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-5">
          <AlertTriangle className="h-8 w-8 text-red-600" />
        </div>
        <h2 className="text-2xl font-display font-bold mb-2">Account Suspended</h2>
        <p className="text-muted-foreground mb-1" data-testid="suspension-overlay-academy">{academyName}</p>
        <p className="text-sm text-muted-foreground mb-6">
          Payment overdue. Complete your subscription renewal to instantly restore Academy,
          Employee, Student and Parent module access.
        </p>
        <div className="space-y-2.5">
          <Button
            onClick={() => navigate('/subscription')}
            data-testid="suspension-overlay-pay"
            className="w-full h-12 text-base bg-emerald-600 hover:bg-emerald-700 rounded-xl"
          >
            <CreditCard className="h-5 w-5 mr-2" /> Make Online Payment
          </Button>
          <Button
            variant="outline"
            onClick={handleManualSync}
            disabled={verifying}
            data-testid="suspension-overlay-refresh"
            className="w-full h-10 text-sm rounded-xl"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${verifying ? 'animate-spin text-emerald-600' : ''}`} />
            {verifying ? 'Verifying payment ledger…' : "I've paid — Check status"}
          </Button>
        </div>
        <p className="text-[10px] text-muted-foreground mt-4">
          Status auto-refreshes every 12 seconds — modal will unlock automatically once payment is verified.
        </p>
      </div>
    </div>
  );
}
